package com.swetdot.swetgram.domain

import kotlinx.serialization.Serializable
import kotlin.time.Instant

/*
 * Client domain models. `IMPLEMENTATION_NOTES.md` §10 requires these to stay separate from the
 * generated wire DTOs, and the separation earns its keep in three concrete ways:
 *
 *  - timestamps are real Instants here, parsed once at the boundary, instead of ISO strings
 *  - `sending` and `failed` exist here but not on the wire, because they are client-local
 *    states the server cannot express
 *  - a field the contract marks optional but the UI always needs can be non-null here, with
 *    the mapper deciding the fallback once rather than at every call site
 *
 * Nothing in this file may import from `com.swetdot.swetgram.protocol`.
 */

data class UserId(val value: String)
data class ChatId(val value: String)
data class MessageId(val value: String)

data class User(
    val id: UserId,
    val username: String,
    val displayName: String,
    val avatarUrl: String?,
    val bio: String?,
    val dateOfBirth: String? = null,
    val showBirthYear: Boolean = true,
    val location: String? = null,
    val isOnline: Boolean,
    /** DECISIONS §4: the client shows an exact last-seen time once a user goes offline. */
    val lastSeenAt: Instant?,
    val isDeleted: Boolean = false,
    val hasBlackBadge: Boolean = false,
) {
    /** Two letters for the avatar placeholder, from the display name rather than the username. */
    val initials: String
        get() = displayName.trim()
            .split(' ')
            .filter { it.isNotBlank() }
            .take(2)
            .joinToString("") { it.first().uppercase() }
            .ifEmpty { "?" }
}

enum class ChatKind { DIRECT, GROUP }

data class Chat(
    val id: ChatId,
    val kind: ChatKind,
    val title: String?,
    val avatarUrl: String?,
    val members: List<User>,
    val lastMessage: Message?,
    val unreadCount: Int,
    val isMuted: Boolean,
    val isArchived: Boolean,
    val isPinned: Boolean,
    val updatedAt: Instant,
) {
    /**
     * A direct chat has no stored title -- it is named after the other participant, which only
     * the client can resolve because only the client knows who "I" am.
     */
    fun displayTitle(me: UserId): String = when (kind) {
        ChatKind.GROUP -> title ?: "Group"
        ChatKind.DIRECT -> peer(me)?.displayName ?: "Unknown"
    }

    /**
     * The other participant in a direct chat, or null if there is not exactly one identifiable.
     *
     * "Not me" is only a safe definition of "them" when I am actually in the room. The server
     * always includes the caller in `members`, so a row where [me] is absent did not come from
     * this account -- in practice a cached conversation left behind by a previous sign-in. Without
     * this guard `firstOrNull { it.id != me }` answers with an arbitrary member and the failure is
     * silent and confident: the signed-in user's own name and online dot rendered as a
     * conversation with themselves. Returning null makes the caller fall back to "Unknown", which
     * is at least true.
     *
     * The cache is cleared on sign-out (see `data/cache/CacheReset`), so this should never fire.
     * It stays because the cost of being wrong here is showing one person another person's
     * conversation, and a row can also arrive from a cache written by an older build.
     */
    fun peer(me: UserId): User? {
        if (kind != ChatKind.DIRECT) return null
        if (members.none { it.id == me }) return null
        return members.firstOrNull { it.id != me }
    }
}

enum class MessageKind { TEXT, IMAGE, VOICE, SYSTEM, SHARED_POST, UNKNOWN }

/**
 * DECISIONS §5 fixes the UI states as sending, sent, read and failed, of which only `sent` and
 * `read` come from the server. There is deliberately NO delivered state in the MVP.
 */
enum class MessageState { SENDING, SENT, READ, FAILED }

data class Message(
    val id: MessageId,
    /** Client-generated. Survives the round trip so an optimistic row can be reconciled. */
    val clientId: String,
    val chatId: ChatId,
    val sender: User,
    val kind: MessageKind,
    val text: String?,
    val imageUrl: String?,
    val voiceUrl: String? = null,
    val voiceDurationMs: Long? = null,
    val voiceWaveform: List<Int> = emptyList(),
    val replyTo: MessagePreview?,
    val state: MessageState,
    val sentAt: Instant,
    val editedAt: Instant?,
    val isDeleted: Boolean,
    val version: Int,
    val canEdit: Boolean,
    val canDeleteForEveryone: Boolean,
    val reactions: List<MessageReaction> = emptyList(),
    val picso: PicsoMessageReference? = null,
) {
    val isEdited: Boolean get() = editedAt != null
    fun isMine(me: UserId): Boolean = sender.id == me
}

data class PicsoMessageReference(val id: String, val expiresAt: String)

@Serializable
data class MessageReaction(
    val emoji: String,
    val count: Int,
    val reactedByMe: Boolean,
)

data class MessagePreview(
    val id: MessageId,
    val senderId: UserId,
    val senderDisplayName: String? = null,
    val text: String?,
    val kind: MessageKind = MessageKind.UNKNOWN,
)

/** A page plus the opaque cursor needed to fetch the next one. */
data class Page<T>(
    val items: List<T>,
    val nextCursor: String?,
    val hasMore: Boolean,
)

data class Session(
    val accessToken: String,
    val refreshToken: String,
    val accessExpiresAt: Instant,
    val user: User,
)

data class ProfileMedia(
    val id: String,
    val url: String,
    val isVideo: Boolean = false,
    val duration: String? = null,
)

data class ProfileLink(
    val url: String,
    val title: String? = null,
)
